import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { bookingService } from '../services/Bookingservice';

const BookingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { vehicle: selectedVehicleData, tripType: initialTripType, serviceType } = location.state || {};

  const [bookingType, setBookingType] = useState(() => {
    if (serviceType === 'airport') return 'Airport Taxi';
    if (serviceType === 'outstation') return 'Outstation';
    if (serviceType === 'local') return 'Local Taxi';
    if (serviceType === 'innova') return 'Innova Taxi';
    return initialTripType || 'Airport Taxi';
  });
  
  const [airportTripType, setAirportTripType] = useState('cityToAirport');
  const [outstationTripType, setOutstationTripType] = useState('oneWay');
  const [localPackageType, setLocalPackageType] = useState('4hr');
  
  // Form fields
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [pickupPoint, setPickupPoint] = useState('');
  const [dropoffPoint, setDropoffPoint] = useState('');
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [dropoffDate, setDropoffDate] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [vehicleDetails, setVehicleDetails] = useState(selectedVehicleData || null);
  
  // Loading and error states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Admin WhatsApp number
  const ADMIN_WHATSAPP = '919150410309'; // Remove + and spaces, use country code without +

  // Local package details
  const localPackages = {
    '4hr': {
      label: '4 Hours / 40 Kms',
      hours: 4,
      kms: 40,
      extraKmRate: 15,
      extraHrRate: 150,
      price: '₹1,300/-'
    },
    '8hr': {
      label: '8 Hours / 80 Kms',
      hours: 8,
      kms: 80,
      extraKmRate: 15,
      extraHrRate: 150,
      price: '₹2,200/-'
    },
    '12hr': {
      label: '12 Hours / 120 Kms',
      hours: 12,
      kms: 120,
      extraKmRate: 15,
      extraHrRate: 150,
      price: '₹3,000/-'
    }
  };

  // Vehicle display names mapping
  const vehicleDisplayNames = {
    'sedan-ac-4+1': 'Swift Dzire (4+1)',
    'sedan-premium-4+1': 'Etios (4+1)',
    'suv-ertiga-6+1': 'SUV Ertiga (6+1)',
    'innova-crysta-7+1': 'Innova Crysta (7+1)',
    'innova-hycross-hybrid-7+1': 'Innova Hycross Hybrid (7+1)'
  };

  // Set vehicle from selectedVehicleData
  useEffect(() => {
    if (selectedVehicleData) {
      setSelectedVehicle(selectedVehicleData.id || selectedVehicleData.name);
      setVehicleDetails(selectedVehicleData);
      
      if (bookingType === 'Local Taxi' && selectedVehicleData.selectedPackage) {
        setLocalPackageType(selectedVehicleData.selectedPackage);
      }

      if (selectedVehicleData.pickupPoint) {
        setPickupPoint(selectedVehicleData.pickupPoint);
      }
      if (selectedVehicleData.dropoffPoint) {
        setDropoffPoint(selectedVehicleData.dropoffPoint);
      }
    }
  }, [selectedVehicleData, bookingType]);

  // Auto-fill based on service type from navigation
  useEffect(() => {
    if (serviceType === 'airport') {
      setBookingType('Airport Taxi');
      setAirportTripType('cityToAirport');
    } else if (serviceType === 'outstation') {
      setBookingType('Outstation');
      setOutstationTripType('oneWay');
    } else if (serviceType === 'local') {
      setBookingType('Local Taxi');
      setLocalPackageType('4hr');
    } else if (serviceType === 'innova') {
      setBookingType('Innova Taxi');
    }
  }, [serviceType]);

  const [airportTerminal, setAirportTerminal] = useState('');

  useEffect(() => {
    if (bookingType === 'Airport Taxi') {
      if (airportTripType === 'cityToAirport') {
        setPickupPoint(''); // Clear for user to type
      } else if (airportTripType === 'airportToCity') {
        setDropoffPoint(''); // Clear for user to type
      }
    }
  }, [airportTripType, bookingType]);

  // Reset form when booking type changes
  useEffect(() => {
    if (bookingType === 'Airport Taxi' && airportTerminal) {
      const airportLocation = `KIA Airport Bangalore - Terminal ${airportTerminal}`;
      
      if (airportTripType === 'cityToAirport') {
        setDropoffPoint(airportLocation);
      } else if (airportTripType === 'airportToCity') {
        setPickupPoint(airportLocation);
      }
    } else if (bookingType === 'Outstation') {
      setOutstationTripType('oneWay');
      setPickupPoint('');
      setDropoffPoint('');
      setDropoffDate('');
    } else if (bookingType === 'Local Taxi') {
      setLocalPackageType('4hr');
      setPickupPoint('');
      setDropoffPoint('');
    } else {
      setPickupPoint('');
      setDropoffPoint('');
    }
  }, [airportTerminal, airportTripType, bookingType]);

  // Format booking details for WhatsApp
  const formatWhatsAppMessage = () => {
    const bookingRef = `BK${Date.now()}${Math.floor(Math.random() * 1000)}`;
    const currentDate = new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
    
    // Get vehicle display name
    const vehicleName = vehicleDisplayNames[selectedVehicle] || selectedVehicle || 'Not specified';
    
    // Format trip type
    let tripTypeText = '';
    if (bookingType === 'Airport Taxi') {
      tripTypeText = airportTripType === 'cityToAirport' ? 'City → Airport' : 'Airport → City';
    } else if (bookingType === 'Outstation') {
      tripTypeText = outstationTripType === 'roundTrip' ? 'Round Trip' : 'One Way';
    } else if (bookingType === 'Local Taxi') {
      tripTypeText = localPackages[localPackageType]?.label || 'Local Package';
    }

    // Calculate estimated fare based on booking type
    let estimatedFare = '';
    if (bookingType === 'Local Taxi' && localPackages[localPackageType]) {
      estimatedFare = localPackages[localPackageType].price;
    } else if (vehicleDetails?.price) {
      estimatedFare = `₹${vehicleDetails.price}/-`;
    } else {
      estimatedFare = 'To be calculated';
    }

    const message = `
🔔 *NEW BOOKING REQUEST* 🔔
━━━━━━━━━━━━━━━━━━━━━━━

📋 *BOOKING REFERENCE*
━━━━━━━━━━━━━━━━━━━━━━━
🆔 *Booking ID:* ${bookingRef}
📅 *Requested on:* ${currentDate}
🚗 *Service Type:* ${bookingType}
📍 *Trip Type:* ${tripTypeText}

👤 *CUSTOMER DETAILS*
━━━━━━━━━━━━━━━━━━━━━━━
👤 *Name:* ${name}
📞 *Mobile:* +91${mobile}
📧 *Email:* ${email || 'Not provided'}

🚙 *VEHICLE DETAILS*
━━━━━━━━━━━━━━━━━━━━━━━
🚘 *Vehicle:* ${vehicleName}
${bookingType === 'Local Taxi' && localPackages[localPackageType] ? `📦 *Package:* ${localPackages[localPackageType].label}\n💰 *Package Price:* ${localPackages[localPackageType].price}\n` : ''}
💰 *Estimated Fare:* ${estimatedFare}

📍 *TRIP DETAILS*
━━━━━━━━━━━━━━━━━━━━━━━
📍 *Pickup Location:* ${pickupPoint}
🎯 *Dropoff Location:* ${dropoffPoint || 'Same as pickup (Local)'}
📅 *Pickup Date:* ${pickupDate}
⏰ *Pickup Time:* ${pickupTime}
${dropoffDate ? `📅 *Return Date:* ${dropoffDate}\n` : ''}

💰 *TRIP FEES APPLICABLE*
━━━━━━━━━━━━━━━━━━━━━━━
${bookingType === 'Local Taxi' ? 
  '• Local Package: ₹50 (Owner) + ₹50 (Customer)' : 
  bookingType === 'Outstation' ?
  '• Outstation: ₹100 (Owner) + ₹100 (Customer)' :
  '• Standard trip fees applicable'
}

📊 *BOOKING STATUS*
━━━━━━━━━━━━━━━━━━━━━━━
📌 *Status:* Pending Confirmation
💳 *Payment:* To be collected
🔄 *Source:* Website Booking

━━━━━━━━━━━━━━━━━━━━━━━
✅ Please confirm availability and contact customer
    `;

    return message.trim();
  };

  const validateForm = () => {
    if (!name.trim()) return 'Name is required';
    if (!mobile.trim()) return 'Mobile number is required';
    if (mobile.trim().length !== 10) return 'Mobile number must be 10 digits';
    if (!/^\d{10}$/.test(mobile.trim())) return 'Mobile number must contain only digits';
    
    if (!pickupPoint.trim()) return 'Pickup point is required';
    
    if (!isLocal && !dropoffPoint.trim()) return 'Dropoff point is required';
    
    if (!pickupDate) return 'Pickup date is required';
    if (!pickupTime) return 'Pickup time is required';
    
    if (isOutstation && outstationTripType === 'roundTrip' && !dropoffDate) {
      return 'Dropoff date is required for round trips';
    }
    
    if (!isLocal && !selectedVehicle) return 'Please select a vehicle';
    
    return null;
  };

 // In your BookingPage.jsx, update the handleSubmit function:

const handleSubmit = async (e) => {
  e.preventDefault();
  setSubmitError('');
  
  const validationError = validateForm();
  if (validationError) {
    setSubmitError(validationError);
    return;
  }

  setIsSubmitting(true);

  try {
    // First, try to send to backend if available
    try {
      const payload = prepareBookingPayload();
      console.log('📤 Sending booking payload:', payload);
      
      const response = await bookingService.createBooking(payload);
      console.log('✅ Booking successful:', response);
    } catch (backendError) {
      console.log('Backend not available, sending via WhatsApp only:', backendError);
    }

    // Format and send WhatsApp message
    const whatsappMessage = formatWhatsAppMessage();
    const encodedMessage = encodeURIComponent(whatsappMessage);
    
    // Create WhatsApp URL
    const whatsappUrl = `https://wa.me/${ADMIN_WHATSAPP}?text=${encodedMessage}`;
    
    // Open WhatsApp in a new tab
    window.open(whatsappUrl, '_blank');

    // Generate booking reference
    const bookingRef = `STC${Math.floor(Math.random() * 9000000) + 1000000}`;

    // Get vehicle display name
    const vehicleName = vehicleDisplayNames[selectedVehicle] || selectedVehicle || 'Not specified';

    // Format date nicely
    const formattedDate = new Date(pickupDate).toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Get estimated fare
    let estimatedFare = '';
    if (bookingType === 'Local Taxi' && localPackages[localPackageType]) {
      estimatedFare = localPackages[localPackageType].price;
    } else if (vehicleDetails?.price) {
      estimatedFare = `₹${vehicleDetails.price}/-`;
    } else {
      estimatedFare = 'To be calculated';
    }

    // Navigate to confirmation page with ALL booking details
    navigate('/booking-confirmation', { 
      state: { 
        bookingDetails: {
          bookingId: bookingRef,
          customerName: name,
          mobile: mobile,
          email: email || 'Not provided',
          bookingType: bookingType,
          pickupPoint: pickupPoint,
          dropoffPoint: dropoffPoint || 'Same as pickup (Local Trip)',
          pickupDate: formattedDate,
          pickupTime: pickupTime,
          vehicleName: vehicleName,
          vehicleDetails: vehicleDetails,
          packageType: isLocal ? localPackages[localPackageType]?.label : null,
          estimatedFare: estimatedFare,
          tripType: isAirport ? airportTripType : (isOutstation ? outstationTripType : null),
          localPackageType: isLocal ? localPackageType : null,
          dropoffDate: dropoffDate || null
        }
      } 
    });
    
  } catch (error) {
    console.error('❌ Booking failed:', error);
    
    const errorMessage = error.message || 'Failed to create booking. Please try again.';
    setSubmitError(errorMessage);
    
    alert(`❌ Booking failed: ${errorMessage}`);
  } finally {
    setIsSubmitting(false);
  }
};

  const prepareBookingPayload = () => {
    const bookingRef = `BK${Date.now()}${Math.floor(Math.random() * 1000)}`;
    
    const basePayload = {
      bookingReference: bookingRef,
      bookingType,
      bookingDate: new Date().toISOString(),
      customerInfo: {
        name: name.trim(),
        mobile: `+91${mobile.trim()}`,
        email: email.trim() || undefined,
      },
      vehicleInfo: {
        selected: selectedVehicle,
        details: vehicleDetails ? {
          id: vehicleDetails.id,
          name: vehicleDetails.name,
          type: vehicleDetails.type,
          seaters: vehicleDetails.seaters,
          bags: vehicleDetails.bags,
          ...(vehicleDetails.price && { price: vehicleDetails.price }),
          ...(vehicleDetails.models && { model: vehicleDetails.models })
        } : null
      },
      tripDetails: {
        pickupPoint: pickupPoint.trim(),
        dropoffPoint: dropoffPoint.trim() || (isLocal ? 'Same as pickup' : ''),
        pickupDateTime: `${pickupDate}T${pickupTime}:00`,
        pickupDate,
        pickupTime,
      },
      status: 'pending',
      paymentStatus: 'pending',
      source: 'website',
      metadata: {
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      }
    };

    if (bookingType === 'Airport Taxi') {
      basePayload.airportDetails = {
        tripType: airportTripType,
        terminal: airportTripType === 'cityToAirport' ? dropoffPoint : pickupPoint,
        flightNumber: '',
      };
      
      basePayload.fareEstimate = {
        baseFare: vehicleDetails?.price || 0,
        tollCharges: 'Extra',
        total: vehicleDetails?.price || 0
      };
      
    } else if (bookingType === 'Outstation') {
      basePayload.outstationDetails = {
        tripType: outstationTripType,
        ...(outstationTripType === 'roundTrip' && { 
          dropoffDate,
          returnDateTime: `${dropoffDate}T${pickupTime}:00`
        }),
        minimumKmPerDay: 300,
        driverBata: 400
      };
      
      basePayload.fareEstimate = {
        ratePerKm: vehicleDetails?.price || 0,
        minimumKm: 300,
        driverBata: 400,
        estimatedTotal: outstationTripType === 'roundTrip' 
          ? 'Based on total distance' 
          : 'Based on one-way distance'
      };
      
    } else if (bookingType === 'Local Taxi') {
      const selectedPackage = vehicleDetails?.packageDetails || localPackages[localPackageType];
      
      basePayload.localDetails = {
        packageType: localPackageType,
        package: {
          hours: selectedPackage.hours,
          kms: selectedPackage.kms,
          price: selectedPackage.price,
          extraKmRate: selectedPackage.extraKm,
          extraHrRate: selectedPackage.extraHr
        }
      };
      
      basePayload.fareEstimate = {
        packagePrice: selectedPackage.price,
        includedKm: selectedPackage.kms,
        includedHours: selectedPackage.hours,
        extraKmRate: selectedPackage.extraKm,
        extraHrRate: selectedPackage.extraHr,
        total: selectedPackage.price
      };
    }

    return basePayload;
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  // Trip Fees Note Component
  const TripFeesNote = () => (
    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
      <h3 className="text-sm font-semibold text-blue-800 mb-2 flex items-center">
        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
        Trip Fees Information
      </h3>
      <div className="space-y-2 text-sm">
        <p className="text-blue-700 font-medium">Local Package (Per Day / Per Duty):</p>
        <p className="text-blue-600 ml-4">• ₹50 from Cab Owner + ₹50 from Customer</p>
        
        <p className="text-blue-700 font-medium mt-2">Outstation (Per Day / Per Duty):</p>
        <p className="text-blue-600 ml-4">• ₹100 from Cab Owner + ₹100 from Customer</p>
        
        <p className="text-xs text-blue-500 mt-2 italic">
          * Trip fees applicable only on confirmed bookings
        </p>
      </div>
    </div>
  );

  const isAirport = bookingType === 'Airport Taxi';
  const isOutstation = bookingType === 'Outstation';
  const isLocal = bookingType === 'Local Taxi';
  const showVehicleSelection = bookingType !== 'Local Taxi';

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header with back button */}
        <div className="mb-6 flex items-center justify-between">
          <button
            onClick={handleGoBack}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {isLocal ? 'Book Local Package' : 'Book Your Ride'}
          </h1>
          <div className="w-16"></div>
        </div>

        <TripFeesNote />

        {/* Service Type Indicator */}
        {serviceType && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700 flex items-center">
              <span className="font-medium mr-2">Quick booking:</span>
              {serviceType === 'airport' && '✈️ Airport Transfer'}
              {serviceType === 'outstation' && '🏞️ Outstation Trip'}
              {serviceType === 'local' && '🏙️ Local Package'}
              {serviceType === 'innova' && '🚐 Innova Taxi'}
            </p>
          </div>
        )}

        {/* Main Form Card */}
        <div className="bg-white rounded-lg shadow-xl overflow-hidden">
          <div className="px-6 py-8 sm:p-10">
            {/* Error message display */}
            {submitError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{submitError}</p>
              </div>
            )}

            {/* WhatsApp Info Banner */}
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center">
              <svg className="w-5 h-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793s.447-1.273.607-1.446c.16-.173.346-.216.462-.216l.332.006c.106.005.249-.04.39.298.144.347.491 1.2.534 1.287.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.087-.177.18-.076.354.101.174.449.741.964 1.201.662.591 1.221.774 1.394.86s.274.072.376-.043c.101-.116.433-.506.549-.68.116-.173.231-.145.39-.087s1.011.477 1.184.564.289.13.332.202c.043.073.043.423-.101.828z"/>
              </svg>
              <p className="text-xs text-green-700">
                <span className="font-semibold">WhatsApp Notification:</span> Your booking details will be sent to our admin via WhatsApp for quick confirmation.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-900 border-b pb-2">Personal Information</h2>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name *</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Your name"
                    disabled={isSubmitting}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Mobile *</label>
                  <div className="mt-1 flex rounded-md shadow-sm">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                      +91
                    </span>
                    <input
                      type="tel"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                      className="flex-1 block w-full border border-gray-300 rounded-none rounded-r-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Mobile number"
                      pattern="[0-9]{10}"
                      maxLength="10"
                      disabled={isSubmitting}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="your@email.com"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              {/* Booking Details */}
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-900 border-b pb-2">Booking Details</h2>
                
                {/* Booking Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Booking Type</label>
                  <select
                    value={bookingType}
                    onChange={(e) => setBookingType(e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    disabled={isSubmitting}
                  >
                    <option value="Airport Taxi">Airport Taxi</option>
                    <option value="Outstation">Outstation</option>
                    <option value="Local Taxi">Local Taxi</option>
                    <option value="Innova Taxi">Innova Taxi</option>
                  </select>
                </div>

                {/* Local Package Selection */}
                {isLocal && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Select Package *</label>
                    <select
                      value={localPackageType}
                      onChange={(e) => setLocalPackageType(e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      disabled={isSubmitting}
                      required
                    >
                      <option value="4hr">4 Hours / 40 Kms</option>
                      <option value="8hr">8 Hours / 80 Kms</option>
                      <option value="12hr">12 Hours / 120 Kms</option>
                    </select>
                  </div>
                )}

                {/* Vehicle Selection */}
                {showVehicleSelection && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Select Vehicle *</label>
                    <select
                      value={selectedVehicle}
                      onChange={(e) => setSelectedVehicle(e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      disabled={isSubmitting}
                      required
                    >
                      <option value="">Select a vehicle</option>
                      {bookingType === 'Innova Taxi' ? (
                        <>
                          <option value="innova-crysta-7+1">Innova Crysta (7+1)</option>
                          <option value="innova-hycross-hybrid-7+1">Innova Hycross Hybrid (7+1)</option>
                        </>
                      ) : (
                        <>
                          <option value="sedan-ac-4+1">Swift Dzire (4+1)</option>
                          <option value="sedan-premium-4+1">Etios (4+1)</option>
                          <option value="suv-ertiga-6+1">SUV Ertiga (6+1)</option>
                        </>
                      )}
                    </select>
                  </div>
                )}

                {/* Trip Type Options */}
                {isAirport && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Trip Type</label>
                    <div className="flex gap-6">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="airportTripType"
                          checked={airportTripType === 'cityToAirport'}
                          onChange={() => setAirportTripType('cityToAirport')}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          disabled={isSubmitting}
                        />
                        <span className="ml-2 text-sm text-gray-700">City to Airport</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="airportTripType"
                          checked={airportTripType === 'airportToCity'}
                          onChange={() => setAirportTripType('airportToCity')}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          disabled={isSubmitting}
                        />
                        <span className="ml-2 text-sm text-gray-700">Airport to City</span>
                      </label>
                    </div>
                  </div>
                )}

                {isOutstation && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Trip Type</label>
                    <div className="flex gap-6">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="outstationTripType"
                          checked={outstationTripType === 'roundTrip'}
                          onChange={() => setOutstationTripType('roundTrip')}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          disabled={isSubmitting}
                        />
                        <span className="ml-2 text-sm text-gray-700">Round Trip</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="outstationTripType"
                          checked={outstationTripType === 'oneWay'}
                          onChange={() => setOutstationTripType('oneWay')}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          disabled={isSubmitting}
                        />
                        <span className="ml-2 text-sm text-gray-700">One Way</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Pickup and Dropoff Points */}
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-900 border-b pb-2">Location Details</h2>
                
                {/* Pickup Point */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Pickup Point *
                    {isAirport && airportTripType === 'airportToCity' && (
                      <span className="ml-2 text-xs text-blue-600">(Airport pickup)</span>
                    )}
                  </label>
                  
                  {isAirport && airportTripType === 'airportToCity' ? (
                    <select
                      value={pickupPoint}
                      onChange={(e) => setPickupPoint(e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      disabled={isSubmitting}
                      required
                    >
                      <option value="">Select airport terminal</option>
                      <option value="KIA Airport Bangalore - Terminal 1 (T1)">Terminal 1 (T1)</option>
                      <option value="KIA Airport Bangalore - Terminal 2 (T2)">Terminal 2 (T2)</option>
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={pickupPoint}
                      onChange={(e) => setPickupPoint(e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter pickup location"
                      disabled={isSubmitting}
                      required
                    />
                  )}
                </div>

                {/* Dropoff Point */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Dropoff Point *
                    {isAirport && airportTripType === 'cityToAirport' && (
                      <span className="ml-2 text-xs text-blue-600">(Airport dropoff)</span>
                    )}
                  </label>
                  
                  {isAirport && airportTripType === 'cityToAirport' ? (
                    <select
                      value={dropoffPoint}
                      onChange={(e) => setDropoffPoint(e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      disabled={isSubmitting}
                      required
                    >
                      <option value="">Select airport terminal</option>
                      <option value="Terminal 1 (T1)">Terminal 1 (T1)</option>
                      <option value="Terminal 2 (T2)">Terminal 2 (T2)</option>
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={dropoffPoint}
                      onChange={(e) => setDropoffPoint(e.target.value)}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter dropoff location"
                      disabled={isSubmitting}
                      required={!isLocal}
                    />
                  )}
                </div>
              </div>

              {/* Date and Time */}
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-900 border-b pb-2">Date & Time</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Pickup Date *</label>
                    <input
                      type="date"
                      value={pickupDate}
                      onChange={(e) => setPickupDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      disabled={isSubmitting}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Pickup Time *</label>
                    <select
                      value={pickupTime}
                      onChange={(e) => setPickupTime(e.target.value)}
                      required
                      disabled={isSubmitting}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select time</option>
                      {Array.from({ length: 24 * 4 }, (_, i) => {
                        const hour = Math.floor(i / 4);
                        const minute = (i % 4) * 15;
                        const period = hour < 12 ? 'AM' : 'PM';
                        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                        const displayMinute = minute.toString().padStart(2, '0');
                        const timeValue = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                        const displayTime = `${displayHour}:${displayMinute} ${period}`;
                        
                        return (
                          <option key={i} value={timeValue}>
                            {displayTime}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>

                {/* Dropoff Date - only for Outstation Round Trip */}
                {isOutstation && outstationTripType === 'roundTrip' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Dropoff Date *</label>
                    <input
                      type="date"
                      value={dropoffDate}
                      onChange={(e) => setDropoffDate(e.target.value)}
                      min={pickupDate}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      disabled={isSubmitting}
                      required
                    />
                  </div>
                )}
              </div>

              {/* Selected Vehicle Summary */}
              {isLocal && vehicleDetails && (
                <div className="bg-blue-50 p-4 rounded-md">
                  <p className="text-sm font-medium text-blue-800">
                    Selected Vehicle: {vehicleDetails.name || 'Sedan'}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    Package: {localPackages[localPackageType].label} - {localPackages[localPackageType].price}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="pt-6 flex gap-4">
                <button
                  type="button"
                  onClick={handleGoBack}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium py-3 px-4 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`flex-1 bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-4 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors flex items-center justify-center ${
                    isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771z"/>
                      </svg>
                      Confirm Booking via WhatsApp
                    </span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
